import { RollingNumber } from '@/components/ui/rolling-number';
import { cn } from '@/lib/utils';
import type { ReactNode } from 'react';

/** Animated numeric metric for PageHero title/subtitle/meta. Rolls from 0 while loading. */
export function PageHeroMetric({
  value,
  format,
  isLoading = false,
  size = 'primary',
  className,
  durationMs,
  'aria-label': ariaLabel,
}: {
  value: number;
  format: (n: number) => string;
  isLoading?: boolean;
  size?: 'primary' | 'secondary';
  className?: string;
  durationMs?: number;
  'aria-label'?: string;
}) {
  const valueClass =
    size === 'primary'
      ? 'text-fin-white text-2xl font-semibold tabular-nums sm:text-3xl'
      : 'text-fin-white text-base font-semibold tabular-nums';

  return (
    <RollingNumber
      value={isLoading ? 0 : value}
      format={format}
      className={cn(valueClass, className)}
      durationMs={durationMs}
      aria-label={ariaLabel ?? format(value)}
    />
  );
}

/**
 * Page-level identity card (icon/avatar + title + meta). One per entity route
 * at the top. `rounded-2xl`, ring, shadow, optional gradient blobs.
 */
export function PageHero({
  title,
  subtitle,
  meta,
  avatar,
  trailingAction,
  className,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  meta?: ReactNode;
  avatar?: ReactNode;
  /** Optional control(s) pinned to the top-right (e.g. settings link). */
  trailingAction?: ReactNode;
  /** Extra classes on the outer section (e.g. `mt-4`). */
  className?: string;
}) {
  return (
    <section
      className={cn(
        'border-fin-gray-800/80 bg-fin-gray-900/60 shadow-fin-black/30 ring-fin-gray-800/50 relative overflow-hidden rounded-2xl border p-6 shadow-lg ring-1 sm:p-8',
        className
      )}
    >
      <div
        className='bg-fin-accent/18 animate-fin-drift pointer-events-none absolute -top-24 -right-24 size-72 rounded-full blur-3xl will-change-transform motion-reduce:animate-none'
        aria-hidden
      />
      <div
        className='bg-fin-purple/18 animate-fin-drift-slow pointer-events-none absolute -bottom-20 -left-16 size-56 rounded-full blur-3xl will-change-transform motion-reduce:animate-none'
        aria-hidden
      />
      {trailingAction ? (
        <div className='absolute top-3 right-3 z-10 sm:top-4 sm:right-4'>{trailingAction}</div>
      ) : null}
      <div className='relative flex flex-row gap-6 sm:flex-row sm:items-start sm:gap-8'>
        {avatar}
        <div className='min-w-0 flex-1'>
          <h1 className='text-fin-white text-2xl font-semibold tracking-tight sm:text-3xl'>
            {title}
          </h1>
          {subtitle ? <div className='mt-2 text-sm sm:mt-2'>{subtitle}</div> : null}
          {meta}
        </div>
      </div>
    </section>
  );
}
